import React, { useState } from "react";

// Material UI
import { Button, Autocomplete, TextField } from "@mui/material";
// Material UI Icons
import MapMarkerRadiusIcon from "mdi-react/MapMarkerRadiusIcon";
import { Translate, getTranslation } from "orion-components/i18n";
import { useDispatch } from "react-redux";

const DockedControls = ({
	userCameras,
	videoTours,
	addToDock,
	cameraPosition,
	dockedCameras,
	setFindNearestMode,
	cameraView,
	findNearestMode,
	findNearestPosition,
	dir
}) => {
	const dispatch = useDispatch();

	const [cameraSearch, setCameraSearch] = useState("");
	const [searchFocused, setSearchFocused] = useState(false);

	const handleUpdateInput = (event, selectedOption) => {
		if (!selectedOption || typeof selectedOption === "string" || selectedOption.type === "header") {
			return;
		}

		setCameraSearch(selectedOption.name);
		dispatch(addToDock(selectedOption.id, cameraPosition, dockedCameras));
	};

	const handleToggleNearestCameraMode = () => {
		dispatch(setFindNearestMode(cameraPosition));
	};

	const searchableCameras = userCameras
		.filter(camera => !dockedCameras.includes(camera.id))
		.map(camera => ({
			id: camera.id,
			name: camera?.entityData?.properties?.name || "",
			type: "camera"
		}));
	const userCameraIds = new Set(userCameras.map(camera => camera.id));
	const searchableVideoTours = Object.values(videoTours || {})
		.filter(videoTour => !dockedCameras.includes(videoTour.id))
		.filter(videoTour => videoTour.cameras?.some(cameraEntry => userCameraIds.has(cameraEntry?.cameraId)))
		.map(videoTour => ({
			id: videoTour.id,
			name: videoTour.name || "",
			type: "videoTour"
		}));
	const filterBySearch = item => item.name.toLowerCase().includes(cameraSearch.toLowerCase());
	const filteredCameras = searchableCameras.filter(filterBySearch);
	const filteredVideoTours = searchableVideoTours.filter(filterBySearch);
	const filteredDockItems = [
		...filteredCameras,
		...(filteredVideoTours.length
			? [
					{
						id: "video-tours-header",
						name: getTranslation("global.dock.cameras.dockedControls.videoTours"),
						type: "header"
					},
					...filteredVideoTours
			  ]
			: [])
	];

	const controlStyles = {
		addCamera: {
			backgroundColor: "#41454A",
			borderRadius: "5px",
			marginTop: "15%",
			fontSize: "10px",
			fontWeight: "bold",
			padding: "6px 0",
			...(findNearestMode[cameraPosition] && findNearestPosition === cameraPosition ? {} : { color: "#fff" })
		},
		searchCamera: {
			backgroundColor: "#2C2B2D"
		},
		dockedControls: {
			fontWeight: "bold",
			...(dir === "rtl" ? { paddingLeft: 16, paddingRight: 8 } : { paddingLeft: 8, paddingRight: 16 })
		},
		mapMarkerRadiusIcon: {
			...(dir === "ltr" && { marginLeft: 12, marginRight: 0 }),
			...(dir === "rtl" && { marginRight: 12, marginLeft: 0 })
		}
	};

	return (
		<div>
			<div className="camera-dock-controls" style={cameraView ? { paddingBottom: "15%" } : {}} data-testid="orion-components-dock-cameras-components-docked-controls-div">
				{cameraView && (
					<Button
						style={controlStyles.addCamera}
						primary={findNearestMode[cameraPosition] && findNearestPosition === cameraPosition}
						onClick={handleToggleNearestCameraMode} data-testid="orion-components-dock-cameras-components-docked-controls-button"
					>
						<MapMarkerRadiusIcon style={controlStyles.mapMarkerRadiusIcon} />
						<span style={controlStyles.dockedControls}>
							{findNearestMode[cameraPosition] && findNearestPosition === cameraPosition
								? getTranslation("global.dock.cameras.dockedControls.selectNearestCam")
								: getTranslation("global.dock.cameras.dockedControls.mapLocation")}
						</span>
					</Button>
				)}
				{cameraView && (
					<p style={{ color: "#fff" }}>
						{" "}
						<Translate value="global.dock.cameras.dockedControls.or" />{" "}
					</p>
				)}
				<div style={{ width: 256 }}>
					<Autocomplete
						freeSolo
						id={`camera-dock-search-${cameraPosition}`}
						className="search-for-camera"
						disablePortal
						renderInput={params => (
							<TextField
								{...params}
								variant="standard"
								placeholder={getTranslation("global.dock.cameras.dockedControls.searchForCam")}
								onFocus={() => setSearchFocused(true)}
								onBlur={() => setSearchFocused(false)} data-testid="orion-components-dock-cameras-components-docked-controls-text-field"
							/>
						)}
						options={filteredDockItems}
						filterOptions={options => options}
						getOptionLabel={option => {
							if (!option) {
								return "";
							} else if (typeof option === "string") {
								return option;
							} else if (Object.keys(option).length === 0) {
								return "";
							} else {
								return option?.name || "";
							}
						}}
						getOptionDisabled={option => option?.type === "header"}
						renderOption={(props, option) => {
							if (option.type === "header") {
								return (
									<li
										{...props}
										style={{
											...props.style,
											color: "#B5B9BE",
											fontSize: 15,
											fontWeight: 600,
											padding: "6px 12px 4px 12px",
											pointerEvents: "none",
											textTransform: "uppercase"
										}} data-testid="orion-components-dock-cameras-components-docked-controls-li"
									>
										{option.name}
									</li>
								);
							}

							return <li {...props} data-testid="orion-components-dock-cameras-components-docked-controls-li-2">{option.name}</li>;
						}}
						open={searchFocused}
						onChange={handleUpdateInput}
						inputValue={cameraSearch}
						onInputChange={(event, value) => setCameraSearch(value)}
						isOptionEqualToValue={(option, value) => option?.id === value?.id}
						componentsProps={{
							popper: {
								sx: {
									zIndex: 2600
								}
							}
						}}
						noOptionsText={getTranslation("global.dock.cameras.dockedControls.noCamerasOrVideoToursFound")}
						disableClearable data-testid="orion-components-dock-cameras-components-docked-controls-autocomplete"
					/>
				</div>
			</div>
		</div>
	);
};

export default DockedControls;
